/**
 * Iterations Tab — v1.1.20
 *
 * Read-only timeline of every deliverable version + the formal QC report that
 * reviewed it. Mirrors what the AI Operator sees on a revision so the founder
 * can audit exactly which context was sent into each round.
 *
 * Layout per round:
 *   ┌─ Round N · Produced 2026-05-06 by gemini-2.5-flash ─────┐
 *   │ Reviewer feedback that triggered this round (if N>1)    │
 *   │ Deliverable text (collapsed, expand to see full)        │
 *   │ Formal QC report — pass/fail, rubric, reviewer comment  │
 *   │ Self-QC summary (if any)                                │
 *   └──────────────────────────────────────────────────────────┘
 */

import { useState } from 'react';
import { Sparkles, ChevronDown, MessageSquare, FileText, ShieldCheck, ShieldX, Zap } from 'lucide-react';
import { LoadingState } from '@/components/ui/LoadingState/LoadingState';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { t } from '@/lib/i18n';
import { formatDateTime } from '@/lib/localeFormatters';
import { useRorosWorkItemIterations } from '@/hooks/roros/control-tower';
import type { IterationRoundDTO, IterationVersionDTO, IterationQcReportDTO } from '@/types/roros/control-tower';

interface IterationsTabProps {
  workItemId: string;
}

export function IterationsTab({ workItemId }: IterationsTabProps) {
  const { data, isLoading, isError } = useRorosWorkItemIterations(workItemId);

  if (isLoading) return <LoadingState text={t('Loading iterations…')} minHeight="120px" />;
  if (isError) return <Alert variant="error" title={t('Failed to load iterations')} />;
  if (!data || data.rounds.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        {t('No deliverable rounds yet — this work item has not been produced by an AI Operator.')}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {data.rounds.length === 1
          ? t('1 round produced so far. The AI Operator sees this entire history when revising.')
          : `${data.rounds.length} ${t('rounds produced so far. The AI Operator sees this entire history when revising.')}`}
      </p>
      {data.rounds.map((round) => (
        <RoundCard key={round.version.id} round={round} />
      ))}
    </div>
  );
}

// ─── Round card ────────────────────────────────────────────────────────────

function RoundCard({ round }: { round: IterationRoundDTO }) {
  const [expanded, setExpanded] = useState(false);
  const v = round.version;

  return (
    <div
      className="border border-border rounded-lg overflow-hidden"
      data-testid={`iteration-round-${round.number}`}
    >
      {/* Header row */}
      <Button
        variant="ghost"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 bg-muted/30 hover:bg-muted/50 h-auto text-left"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="h-3.5 w-3.5 text-primary flex-shrink-0" aria-hidden />
          <span className="text-sm font-semibold">
            {t('Round')} {round.number}
          </span>
          {round.qcReport && (
            <Badge variant={round.qcReport.passed ? 'success' : 'error'} className="text-xs">
              {round.qcReport.passed ? t('QC passed') : t('QC failed')}
            </Badge>
          )}
          {!round.qcReport && (
            <Badge variant="default" className="text-xs">
              {t('No QC report')}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0">
          <span>{formatDateTime(v.createdAt).date}</span>
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </div>
      </Button>

      {/* Expanded body */}
      {expanded && (
        <div className="px-3 py-3 space-y-4 border-t border-border">
          {/* Triggering feedback */}
          {v.triggeringFeedback && (
            <Section icon={<MessageSquare className="h-3.5 w-3.5" />} title={t('Reviewer feedback that triggered this round')}>
              <p className="text-sm whitespace-pre-wrap text-foreground">{v.triggeringFeedback}</p>
            </Section>
          )}

          {/* Deliverable */}
          <Section
            icon={<FileText className="h-3.5 w-3.5" />}
            title={`${t('Deliverable produced')}${v.llmModel ? ` · ${v.llmModel}` : ''}`}
          >
            <pre className="text-sm whitespace-pre-wrap font-sans bg-muted/20 rounded-md border border-border p-2 max-h-72 overflow-y-auto [scrollbar-width:thin]">
              {v.deliverableText}
            </pre>
          </Section>

          {/* Formal QC report */}
          {round.qcReport && <QcReportBlock report={round.qcReport} />}

          {/* Self-QC */}
          <SelfQcBlock selfQc={v.qcSelfReport} />
        </div>
      )}
    </div>
  );
}

// ─── Sections ──────────────────────────────────────────────────────────────

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
        {icon}
        {title}
      </p>
      {children}
    </div>
  );
}

function QcReportBlock({ report }: { report: IterationQcReportDTO }) {
  const checks = report.checkResults && typeof report.checkResults === 'object' ? report.checkResults : null;
  const checksKeys = checks ? Object.keys(checks as Record<string, unknown>) : [];

  return (
    <Section
      icon={report.passed ? <ShieldCheck className="h-3.5 w-3.5 text-success" /> : <ShieldX className="h-3.5 w-3.5 text-error" />}
      title={t('Formal QC report')}
    >
      <div className="text-sm space-y-2">
        <div className="flex items-center gap-2">
          <Badge variant={report.passed ? 'success' : 'error'} className="text-xs">
            {report.passed ? t('Passed') : t('Failed')}
          </Badge>
          {report.fttPassed && (
            <Badge variant="default" className="text-xs">{t('First-pass')}</Badge>
          )}
        </div>
        {report.reworkFeedback && (
          <div className="bg-muted/20 rounded-md border border-border p-2">
            <p className="text-xs text-muted-foreground mb-1">{t('Reviewer feedback')}</p>
            <p className="whitespace-pre-wrap">{report.reworkFeedback}</p>
          </div>
        )}
        {checksKeys.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              {t('Rubric / check results')} ({checksKeys.length})
            </summary>
            <pre className="mt-1 bg-muted/20 rounded-md border border-border p-2 overflow-x-auto whitespace-pre-wrap [scrollbar-width:thin]">
              {JSON.stringify(report.checkResults, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </Section>
  );
}

function SelfQcBlock({ selfQc }: { selfQc: IterationVersionDTO['qcSelfReport'] }) {
  if (!selfQc || typeof selfQc !== 'object') return null;
  const sq = selfQc as { finalPassed?: boolean; iterations?: number; criteria?: Array<{ name: string; passed: boolean; reason?: string }> };
  const criteria = Array.isArray(sq.criteria) ? sq.criteria : [];

  if (criteria.length === 0 && sq.finalPassed === undefined) return null;

  return (
    <Section icon={<Zap className="h-3.5 w-3.5" />} title={t('Self-QC at production time')}>
      <div className="text-sm space-y-1.5">
        <div className="flex items-center gap-2">
          <Badge variant={sq.finalPassed ? 'success' : 'default'} className="text-xs">
            {sq.finalPassed ? t('Passed') : t('Did not pass')}
          </Badge>
          {sq.iterations !== undefined && (
            <span className="text-xs text-muted-foreground">
              {sq.iterations} {sq.iterations === 1 ? t('internal iteration') : t('internal iterations')}
            </span>
          )}
        </div>
        {criteria.length > 0 && (
          <ul className="text-xs space-y-0.5">
            {criteria.map((c, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className={c.passed ? 'text-success' : 'text-error'}>{c.passed ? '✓' : '✗'}</span>
                <span>
                  <span className="font-medium">{c.name}</span>
                  {c.reason ? <span className="text-muted-foreground"> — {c.reason}</span> : null}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  );
}
